<!--suppress VueUnrecognizedSlot -->

<script setup lang="ts">
import axios from "axios";
import { endpoint } from "@/remote/utils";
import { computed, onMounted, ref, shallowRef } from "vue";
import { LightDoMaintenance } from "@/types/do_maintenance.ts";
import _ from "lodash";
import { comparatorICase } from "@/utils";
import ViewDoMaintenance from "@/components/ViewDoMaintenance.vue";

const doMaints = shallowRef<LightDoMaintenance[]>([]);
const loading = ref(true);
const sortBy = ref([{ key: 'id', order: 'desc' }]);
const parks = computed(() => {
  return _.sortedUniq(doMaints.value.map((dom) => dom.park_name).sort((a, b) => comparatorICase(a, b)));
});
const filterPark = ref("");
const overlay = ref(false);
const overlayMaintId = ref<number | null>(null);
const cardLoading = ref(false);

function showDataOverlay(maintId: number) {
  overlayMaintId.value = maintId;
  cardLoading.value = true;
  overlay.value = true;
}

async function fetchDoMaintList() {
  const res = await axios.get(`${endpoint("internal/current/domaintenances/")}`, {
    withCredentials: true
  });
  return res.data;
}

const deleting = ref(false);

async function deleteDoMaint(maintId: number) {
  deleting.value = true;
  try {
    await axios.delete(`${endpoint(`internal/current/domaintenances/${maintId}/`)}`, {
      withCredentials: true
    });
    doMaints.value = await fetchDoMaintList();
  } finally {
    deleting.value = false;
  }
}

onMounted(async () => {
  doMaints.value = await fetchDoMaintList();
  loading.value = false;
});

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Park Name', key: 'park_name' },
  { title: 'Time of Execution', key: 'execution_time' },
  { title: 'Curative', key: 'curative', align: 'end' },
  { title: 'Preventive', key: 'preventive', align: 'end' },
  { title: 'Actions', key: 'actions', align: 'end', sortable: false }
];
</script>

<template>
  <div class="pa-2">
    <h2 class="text-center">Show Done Maintenance</h2>
    <v-row>
      <v-col>
        <v-autocomplete
          label="Filter by Park" density="compact"
          :items="parks" v-model="filterPark"
          max-width="400" class="mx-auto"
          clearable
        />
      </v-col>
    </v-row>
    <!-- @vue-expect-error Wrong typing from Vuetify, this works -->
    <v-data-table
      v-model:search="filterPark"
      :filter-keys="['park_name']"
      v-model:sort-by="sortBy"
      :items="doMaints"
      :headers="headers"
      :loading="loading"
      density="compact"
    >
      <template v-slot:item.curative="{value}">
        <div class="d-flex justify-end">
          <v-icon v-if="value !== null" icon="mdi-check-circle" color="green"/>
          <v-icon v-else icon="mdi-close-circle" color="red"/>
        </div>
      </template>
      <template v-slot:item.preventive="{value}">
        <div class="d-flex justify-end">
          <v-icon v-if="value !== null" icon="mdi-check-circle" color="green"/>
          <v-icon v-else icon="mdi-close-circle" color="red"/>
        </div>
      </template>
      <template v-slot:item.execution_time="{value}">
        {{ (new Date(value)).toLocaleString('sv') }}
      </template>
      <template v-slot:item.actions="{item}">
        <div class="d-flex justify-end ga-2">
          <v-btn variant="tonal" density="compact" icon="mdi-magnify" v-tooltip="'Details'"
                 @click="showDataOverlay(item.id)"/>
          <v-dialog max-width="500" :close-on-back="!deleting">
            <template v-slot:activator="{ props: activatorProps }">
              <v-btn
                v-bind="activatorProps"
                variant="tonal"
                density="compact"
                icon="mdi-delete-outline"
                v-tooltip="'Delete'"
                color="error"
              />
            </template>

            <template v-slot:default="{ isActive }">
              <v-card>
                <v-card-title>
                  <span class="text-red"><v-icon icon="mdi-alert"/> Confirm Deletion!</span>
                </v-card-title>
                <v-card-text>
                  <p>Are you sure you want to delete this maintenance?</p>
                  <p style="font-weight: bold">THIS OPERATION IS IRREVERSIBLE!</p>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    text="Delete"
                    variant="tonal"
                    color="error"
                    :loading="deleting"
                    @click="deleteDoMaint(item.id)"
                  />
                  <v-spacer></v-spacer>
                  <v-btn
                    text="Keep"
                    variant="tonal"
                    @click="isActive.value = false"
                  />
                </v-card-actions>
              </v-card>
            </template>
          </v-dialog>
        </div>
      </template>
    </v-data-table>
    <v-overlay v-model="overlay" class="align-center justify-center" content-class="my-content-overlay">
      <v-card class="overflow-auto h-100 w-100" :loading="cardLoading">
        <v-card-title class="text-center">Maintenance ID {{ overlayMaintId }}</v-card-title>
        <v-card-text>
          <ViewDoMaintenance
            v-if="overlayMaintId !== null"
            :do-maint-id="overlayMaintId"
            @loaded="cardLoading = false"
          />
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>

<style>
/*noinspection CssUnusedSymbol*/
.my-content-overlay {
  height: 90%;
  width: 90%;
}
</style>
